import { Team, ModuleId, DEFAULT_MODULE_ORDER } from '@/types/team';
import { normalizeTaskListLayout } from '@/logic/taskListLayoutLogic';

/**
 * Utility for mapping between Supabase Database rows and Frontend Team objects.
 * Centralizing this ensures that schema changes only need to be updated in one place.
 */

// Explicit allowlist of columns read from the `teams` table. Keeps the wire payload
// in sync with `dbRowToTeam` below, and prevents future sensitive columns added to
// `teams` (billing, internal flags, audit fields, …) from auto-flowing to clients.
// Whenever you add a field to `dbRowToTeam`, add it here too.
export const TEAM_COLUMNS =
  'id, name, slug, slogan, image_url, created_at, created_by_user_id, ' +
  'weekend_start_day, weekend_start_hour, weekend_end_day, weekend_end_hour, ' +
  'morning_hour, evening_hour, show_fyp_module, show_tasks_module, ' +
  'show_completed_module, show_grocery_module, show_stock_module, ' +
  'show_meals_module, show_health_module, show_goals_module, module_order, language, ' +
  'theme_primary_color, action_button_bg_color, action_button_icon_color, ' +
  'is_public, invite_code, invite_code_enabled, task_list_layout';

export const dbRowToTeam = (row: any): Team => {
  // Load localStorage settings for module labels and tag icons (DB columns don't exist)
  const storedModuleLabels = localStorage.getItem(`team-module-labels-${row.id}`);
  const storedTagIcons = localStorage.getItem(`team-tag-icons-${row.id}`);

  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    slogan: row.slogan,
    imageUrl: row.image_url,
    createdAt: new Date(row.created_at),
    createdByUserId: row.created_by_user_id || null,
    weekendStartDay: row.weekend_start_day ?? 5, // Default: Friday
    weekendStartHour: row.weekend_start_hour ?? 17, // Default: 17:00
    weekendEndDay: row.weekend_end_day ?? 0, // Default: Sunday
    weekendEndHour: row.weekend_end_hour ?? 0, // Default: midnight
    morningHour: row.morning_hour ?? 6, // Default: 06:00
    eveningHour: row.evening_hour ?? 18, // Default: 18:00
    showFypModule: row.show_fyp_module ?? true,
    showTasksModule: row.show_tasks_module ?? true,
    showCompletedModule: row.show_completed_module ?? true,
    showGroceryModule: row.show_grocery_module ?? true,
    showStockModule: row.show_stock_module ?? true,
    showMealsModule: row.show_meals_module ?? true,
    showHealthModule: row.show_health_module ?? true,
    showGoalsModule: row.show_goals_module ?? false,
    moduleOrder: (() => {
      const dbOrder = (row.module_order as ModuleId[]) || DEFAULT_MODULE_ORDER;
      // Ensure 'fyp' is present. If missing, prepend it (as per requirement "first tab on the left")
      const orderWithFyp = dbOrder.includes('fyp') ? dbOrder : ['fyp', ...dbOrder];
      // Ensure all other default modules are present (appended if missing)
      return [...new Set([...orderWithFyp, ...DEFAULT_MODULE_ORDER])] as ModuleId[];
    })(),
    moduleLabels: storedModuleLabels ? JSON.parse(storedModuleLabels) : {},
    tagIcons: storedTagIcons ? JSON.parse(storedTagIcons) : {},
    language: (row.language as 'nl' | 'en') || 'nl',
    themePrimaryColor: row.theme_primary_color || '#57876b',
    actionButtonBgColor: row.action_button_bg_color || '#344e41',
    actionButtonIconColor: row.action_button_icon_color || '#ffffff',
    isPublic: row.is_public ?? false,
    inviteCode: row.invite_code ?? null,
    inviteCodeEnabled: row.invite_code_enabled ?? false,
    taskListLayout: normalizeTaskListLayout(row.task_list_layout),
  };
};

export const mapTeamToDb = (updates: Partial<Team>): any => {
  const dbUpdates: any = {};

  if ('name' in updates) dbUpdates.name = (updates.name as string).trim();
  if ('slogan' in updates) dbUpdates.slogan = updates.slogan;
  if ('imageUrl' in updates) dbUpdates.image_url = updates.imageUrl;
  if ('slug' in updates) dbUpdates.slug = updates.slug;
  if ('weekendStartDay' in updates) dbUpdates.weekend_start_day = updates.weekendStartDay;
  if ('weekendStartHour' in updates) dbUpdates.weekend_start_hour = updates.weekendStartHour;
  if ('weekendEndDay' in updates) dbUpdates.weekend_end_day = updates.weekendEndDay;
  if ('weekendEndHour' in updates) dbUpdates.weekend_end_hour = updates.weekendEndHour;
  if ('morningHour' in updates) dbUpdates.morning_hour = updates.morningHour;
  if ('eveningHour' in updates) dbUpdates.evening_hour = updates.eveningHour;
  if ('showFypModule' in updates) dbUpdates.show_fyp_module = updates.showFypModule;
  if ('showTasksModule' in updates) dbUpdates.show_tasks_module = updates.showTasksModule;
  if ('showCompletedModule' in updates) dbUpdates.show_completed_module = updates.showCompletedModule;
  if ('showGroceryModule' in updates) dbUpdates.show_grocery_module = updates.showGroceryModule;
  if ('showStockModule' in updates) dbUpdates.show_stock_module = updates.showStockModule;
  if ('showMealsModule' in updates) dbUpdates.show_meals_module = updates.showMealsModule;
  if ('showHealthModule' in updates) dbUpdates.show_health_module = updates.showHealthModule;
  if ('showGoalsModule' in updates) dbUpdates.show_goals_module = updates.showGoalsModule;
  if ('moduleOrder' in updates) dbUpdates.module_order = updates.moduleOrder;
  if ('language' in updates) dbUpdates.language = updates.language;
  if ('themePrimaryColor' in updates) dbUpdates.theme_primary_color = updates.themePrimaryColor;
  if ('actionButtonBgColor' in updates) dbUpdates.action_button_bg_color = updates.actionButtonBgColor;
  if ('actionButtonIconColor' in updates) dbUpdates.action_button_icon_color = updates.actionButtonIconColor;
  if ('isPublic' in updates) dbUpdates.is_public = updates.isPublic;
  if ('inviteCode' in updates) dbUpdates.invite_code = updates.inviteCode;
  if ('inviteCodeEnabled' in updates) dbUpdates.invite_code_enabled = updates.inviteCodeEnabled;
  if ('taskListLayout' in updates) dbUpdates.task_list_layout = updates.taskListLayout;

  return dbUpdates;
};
